import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { 
  Bot, X, Send, BrainCircuit, Sparkles, 
  ChevronRight, Calendar, Users, DollarSign, Activity 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../context/NotificationContext';
import './AICopilot.css';

const AICopilot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '¡Hola! Soy GDT AI, tu asistente inteligente. ¿En qué puedo ayudarte hoy con la gestión de la clínica?' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [contextData, setContextData] = useState({
    appointments: [],
    patients: [],
    transactions: [],
    stats: {}
  });
  const [patientContext, setPatientContext] = useState(null);
  
  const { addNotification } = useNotifications();
  const chatEndRef = useRef(null);
  
  // Try to get API key from localStorage or default
  const apiKey = localStorage.getItem('gdt_ai_key') || '';

  function scrollToBottom() {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  async function fetchContextData() {
    try {
      const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
      const staffName = activeStaff.full_name || '';

      // Fetch core data for AI context
      let appsQuery = supabase.from('appointments').select('*').limit(50);
      let patsQuery = supabase.from('patients').select('*').limit(20);
      let txsQuery = supabase.from('transactions').select('*').limit(20);

      if (staffName) {
        appsQuery = appsQuery.eq('professional_name', staffName);
        patsQuery = patsQuery.eq('professional_name', staffName);
        txsQuery = txsQuery.eq('professional_name', staffName);
      }

      const { data: apps } = await appsQuery;
      const { data: pats } = await patsQuery;
      const { data: txs } = await txsQuery;
      
      setContextData({
        appointments: apps || [],
        patients: pats || [],
        transactions: txs || [],
        stats: {
          totalApps: apps?.length || 0,
          totalPatients: pats?.length || 0,
          balance: txs?.reduce((acc, t) => acc + (t.type === 'ingreso' ? Number(t.amount) : -Number(t.amount)), 0) || 0
        }
      });
    } catch (err) {
      console.error("Error fetching AI context:", err);
    }
  }

  useEffect(() => {
    if (isOpen) {
      Promise.resolve().then(() => {
        fetchContextData();
      });
      scrollToBottom();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handlePatientContext = (e) => {
      setPatientContext(e.detail);
    };
    window.addEventListener('ai_patient_context', handlePatientContext);
    return () => window.removeEventListener('ai_patient_context', handlePatientContext);
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Re-read API key and saved configuration
    const currentApiKey = localStorage.getItem('gdt_ai_key') || '';
    const savedModel = localStorage.getItem('gdt_ai_model') || 'gemini-1.5-flash';
    const savedVersion = localStorage.getItem('gdt_ai_version') || 'v1beta';
    
    if (currentApiKey === 'demo') {
      const userMessage = input;
      setInput('');
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      setIsLoading(true);
      
      setTimeout(() => {
        const demoResponses = [
          "¡Hola! En modo demo puedo decirte que hoy tienes 5 turnos programados.",
          "El saldo actual de la caja es de $51.700 según los registros.",
          "Claro, puedo ayudarte a agendar un nuevo paciente. ¿Para qué especialidad?",
          "He analizado las tendencias y el 20% de los pacientes suelen cancelar los lunes."
        ];
        const randomResp = demoResponses[Math.floor(Math.random() * demoResponses.length)];
        setMessages(prev => [...prev, { role: 'assistant', content: `[MODO DEMO] ${randomResp}` }]);
        setIsLoading(false);
      }, 1000);
      return;
    }

    if (!currentApiKey) {
      setMessages(prev => [...prev, { role: 'user', content: input }]);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ No he detectado una API Key de Gemini configurada. Por favor, ve a Configuración > IA para activarme.' 
      }]);
      setInput('');
      return;
    }

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const genAI = new GoogleGenerativeAI(currentApiKey.trim());
      
      const patientContextStr = patientContext ? `\nCONTEXTO DE PACIENTE ACTUAL EN PANTALLA:
        - Nombre: ${patientContext.patient.full_name}
        - DNI: ${patientContext.patient.dni}
        - Evoluciones clínicas registradas: ${JSON.stringify(patientContext.evolutions.map(e => ({ fecha: e.created_at, nota: e.content, profesional: e.professional_name })))}` : '';

      const systemPrompt = `Eres GDT AI, el asistente experto de la plataforma GDT (Gestión de Turnos).
        Tu tono es profesional, analítico y servicial. Siempre saluda como GDT AI.
        
        DATOS ACTUALES DE LA CLÍNICA:
        - Fecha de hoy: ${new Date().toLocaleDateString()}
        - Turnos recientes: ${JSON.stringify(contextData.appointments.slice(0, 10).map(a => ({ p: a.patient_name, d: a.appointment_date, t: a.appointment_time, s: a.status })))}
        - Saldo actual: $${contextData.stats.balance}
        - Total pacientes: ${contextData.stats.totalPatients}
        ${patientContextStr}
        
        CAPACIDADES:
        1. Analizar tendencias (ej: huecos en agenda).
        2. Resumir estado financiero.
        3. Agendamiento: Di "Claro, voy a agendar a [NOMBRE]..." y agrega:
        [ACTION:BOOK_TURN]{ "patient_name": "Nombre", "study_type": "Estudio", "date": "YYYY-MM-DD", "time": "HH:MM" }[/ACTION]
        4. Si el usuario pide un resumen del paciente actual, utiliza el CONTEXTO DE PACIENTE ACTUAL para brindarle un resumen médico profesional y conciso de su historia clínica y evoluciones.`;

      // Helper for robust discovery
      const attemptChat = async (mName) => {
        console.log(`[GDT AI] Intentando: ${mName}...`);
        const model = genAI.getGenerativeModel({ model: mName });
        
        // Build history from messages state
        const history = [
          { role: "user", parts: [{ text: systemPrompt }] },
          { role: "model", parts: [{ text: "Entendido. Soy GDT AI, el Copilot operativo de la clínica. Estoy listo para asistir." }] }
        ];

        // Add last 4 messages for context (excluding the system one)
        messages.slice(-4).forEach(msg => {
          history.push({
            role: msg.role === 'user' ? 'user' : 'model',
            parts: [{ text: msg.content }]
          });
        });

        const chat = model.startChat({ history });
        const result = await chat.sendMessage(userMessage);
        const response = await result.response;
        return { text: response.text(), model: mName, version: 'v1' };
      };

      let successData = null;
      const modelCandidates = [
        savedModel, // Try saved one first
        "gemini-2.0-flash", 
        "gemini-flash-latest", 
        "gemini-1.5-flash",
        "gemini-pro-latest",
        "gemini-1.5-pro"
      ];

      // Multi-layer fallback loop
      for (const mName of modelCandidates) {
        if (!mName) continue;
        try {
          successData = await attemptChat(mName);
          if (successData) break;
        } catch (err) {
          console.warn(`[GDT AI] Falló ${mName}:`, err.message);
          // If it's a quota error, don't keep trying models, just wait
          if (err.message?.includes('quota')) throw err;
        }
      }

      if (!successData) {
        throw new Error("No se pudo establecer conexión con ningún modelo de Gemini. Verifica tu API Key.");
      }

      // Save working config
      localStorage.setItem('gdt_ai_model', successData.model);
      localStorage.setItem('gdt_ai_version', successData.version);

      const text = successData.text;

      // Check for actions
      const actionMatch = text.match(/\[ACTION:BOOK_TURN\](.*?)\[\/ACTION\]/s);
      if (actionMatch) {
        try {
          const actionData = JSON.parse(actionMatch[1]);
          await executeBookTurn(actionData);
        } catch (e) {
          console.error("Failed to parse action data", e);
        }
      }

      setMessages(prev => [...prev, { role: 'assistant', content: text.replace(/\[ACTION:.*?\]/gs, '').trim() }]);
    } catch (err) {
      console.error("Detailed AI Error:", err);
      let errorMsg = 'Lo siento, ha ocurrido un error al procesar tu solicitud.';
      
      if (err.message?.includes('API key not valid') || err.message?.includes('API_KEY_INVALID')) {
        errorMsg = '❌ La API Key no es válida. Por favor, revísala en Configuración > IA.';
      } else if (err.message?.includes('quota')) {
        errorMsg = '⏳ Se ha alcanzado el límite de cuota gratuita. Intenta de nuevo en un momento.';
      } else {
        errorMsg = `⚠️ Error: ${err.message || 'Revisa tu conexión y API Key.'}`;
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const executeBookTurn = async (data) => {
    try {
      const { error } = await supabase.from('appointments').insert([{
        patient_name: data.patient_name,
        patient_email: data.patient_email || '',
        patient_phone: data.patient_phone || '',
        study_type: data.study_type,
        appointment_date: data.date,
        appointment_time: data.time,
        status: 'pendiente'
      }]);

      if (error) throw error;

      addNotification({
        title: 'Turno Agendado por IA',
        message: `${data.patient_name} agendado para ${data.study_type} el ${data.date}`,
        type: 'success'
      });
    } catch (err) {
      console.error("Error executing AI action:", err);
      addNotification({
        title: 'Error en Agendamiento IA',
        message: 'No pude concretar la reserva automáticamente.',
        type: 'error'
      });
    }
  };

  return (
    <div className="ai-copilot-container">
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="ai-chat-window"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            <div className="ai-chat-header">
              <div className="ai-brand">
                <div className="ai-icon-bg">
                  <BrainCircuit size={20} />
                </div>
                <div>
                  <h4>GDT AI</h4>
                  <span className="online-indicator">● Activo</span>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="close-chat">
                <X size={20} />
              </button>
            </div>

            <div className="ai-messages-container">
              {messages.map((msg, i) => (
                <div key={i} className={`message ${msg.role}`}>
                  <div className="message-content">
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="message assistant loading">
                  <div className="dot-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <form className="ai-chat-input" onSubmit={handleSendMessage}>
              <input 
                type="text" 
                placeholder="Pregúntame algo..." 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoFocus
              />
              <button type="submit" disabled={isLoading || !input.trim()}>
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button 
        className={`ai-fab ${isOpen ? 'open' : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X size={24} /> : <Bot size={28} />}
        {!isOpen && <div className="ai-pulse"></div>}
      </button>
    </div>
  );
};

export default AICopilot;
